import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, AlertCircle, Shield, ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PasswordStrength {
  score: number;
  message: string;
  color: string;
}

const PartnerPasswordSetup: React.FC = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({ score: 0, message: '', color: 'bg-gray-200' });
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string; general?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Check if user is authenticated
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        // Not authenticated - redirect to partner login
        navigate('/partner-login', { 
          state: { message: 'Please log in to set up your password.' }
        });
      }
    } catch (error: any) {
      console.error('Authentication check error:', error);
      navigate('/partner-login');
    }
  };

  // Password strength evaluation
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength({ score: 0, message: '', color: 'bg-gray-200' });
      return;
    }

    let score = 0;
    let message = '';
    let color = 'bg-red-500';

    // Length check
    if (newPassword.length >= 8) score++;
    if (newPassword.length >= 12) score++;

    // Complexity checks
    if (/[a-z]/.test(newPassword)) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword)) score++;
    if (/[^a-zA-Z0-9]/.test(newPassword)) score++;

    // Determine strength message and color
    if (score <= 2) {
      message = 'Weak - Add more characters and variety';
      color = 'bg-red-500';
    } else if (score <= 4) {
      message = 'Fair - Add special characters for better security';
      color = 'bg-amber-500';
    } else if (score <= 5) {
      message = 'Good - Your password is secure';
      color = 'bg-green-500';
    } else {
      message = 'Excellent - Very strong password';
      color = 'bg-green-600';
    }

    setPasswordStrength({ score, message, color });
  }, [newPassword]);

  // Password requirements check
  const passwordRequirements = [
    { met: newPassword.length >= 8, text: 'At least 8 characters' },
    { met: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
    { met: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
    { met: /[0-9]/.test(newPassword), text: 'One number' },
    { met: /[^a-zA-Z0-9]/.test(newPassword), text: 'One special character' }
  ];

  const validatePasswords = (): boolean => {
    const newErrors: { newPassword?: string; confirmPassword?: string } = {};

    // Validate new password
    if (!newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (passwordStrength.score < 3) {
      newErrors.newPassword = 'Password is too weak. Please use a stronger password';
    }

    // Validate confirm password
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Update password in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      // Success - show completion message
      setSetupComplete(true);

      // Start countdown redirect
      let countdown = 3;
      const countdownInterval = setInterval(() => {
        countdown--;
        setRedirectCountdown(countdown);
        
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          navigate('/partner-dashboard');
        }
      }, 1000);

    } catch (error: any) {
      console.error('Password setup error:', error);
      setErrors({ 
        general: error.message || 'Failed to set up password. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (setupComplete) {
    return (
      <>
        <Helmet>
          <title>Password Setup Complete - TravelHeroes Partner</title>
        </Helmet>

        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                TravelHeroes
              </h1>
              <p className="text-gray-600">Partner Portal</p>
            </div>

            {/* Success Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 mb-6 rounded-full bg-green-100">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                
                <h2 className="text-2xl font-bold text-gray-900 mb-3">
                  Password Set Successfully!
                </h2>
                
                <p className="text-gray-600 mb-6">
                  Your account is now secure and ready to use.
                </p>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-blue-700 mb-2">
                    <ArrowRight className="w-5 h-5" />
                    <span className="font-semibold">Redirecting in {redirectCountdown} seconds</span>
                  </div>
                  <p className="text-sm text-blue-600">
                    Taking you to your partner dashboard...
                  </p>
                </div>

                <button
                  onClick={() => navigate('/partner-dashboard')}
                  className="w-full inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Go to Dashboard Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Security Notice */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <div className="flex items-center justify-center space-x-1 mb-1">
                <Shield className="w-3 h-3" />
                <span>Secured by Supabase Authentication</span>
              </div>
              <p>© 2026 TravelHeroes. All rights reserved.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Set Up Password - TravelHeroes Partner</title>
        <meta name="description" content="Set up your password for TravelHeroes partner account" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              TravelHeroes
            </h1>
            <p className="text-gray-600">Partner Portal</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-600">Account Verified</span>
                <span className="text-sm font-medium text-gray-500">Complete Setup</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '50%' }}></div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-blue-100">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Set Up Your Password
              </h2>
              <p className="text-gray-600">
                Create a secure password to protect your account
              </p>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errors.general}</p>
              </div>
            )}

            {/* Password Setup Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full pl-10 pr-10 py-3 border ${errors.newPassword ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                )}

                {/* Password Strength Indicator */}
                {newPassword && (
                  <div className="mt-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Password Strength</span>
                      <span className="text-xs font-medium text-gray-700">{passwordStrength.message}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full pl-10 pr-10 py-3 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Password Requirements
                </h4>
                <ul className="space-y-2">
                  {passwordRequirements.map((req, index) => (
                    <li key={index} className="flex items-center text-sm">
                      {req.met ? (
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0" />
                      )}
                      <span className={req.met ? 'text-green-700' : 'text-gray-600'}>
                        {req.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Setting Up Password...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            {/* Help Text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Need help?{' '}
                <a href="mailto:support@travelheroes.at" className="text-blue-600 hover:text-blue-700 font-medium">
                  Contact Support
                </a>
              </p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <div className="flex items-center justify-center space-x-1 mb-1">
              <Shield className="w-3 h-3" />
              <span>Secured by Supabase Authentication</span>
            </div>
            <p>© 2026 TravelHeroes. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnerPasswordSetup;